import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Loader2, Users, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Avatar } from "@/components/Avatar";
import { VerifiedBadge } from "@/components/VerifiedBadge";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function NewGroupModal({ open, onClose, onCreated }) {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const { data: friends = [] } = useQuery({
    queryKey: ["friends"],
    queryFn: async () => (await api.get("/friends")).data,
    enabled: open,
  });

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const create = async () => {
    if (!name.trim() || selected.length < 1) return;
    setBusy(true);
    try {
      const { data } = await api.post("/groups", { name, member_ids: selected });
      qc.invalidateQueries({ queryKey: ["groups"] });
      toast.success("Group created!");
      setName(""); setSelected([]);
      onCreated?.(data);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create group");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="new-group-modal" className="bg-card border-2 border-[#6C0BA9] rounded-3xl max-w-sm">
        <DialogTitle className="font-heading text-lg font-extrabold flex items-center gap-2">
          <Users className="w-5 h-5 text-[#C084FC]" /> New Group
        </DialogTitle>
        <Input data-testid="group-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name" className="bg-input border-border rounded-xl" />
        <div className="max-h-[40vh] overflow-y-auto space-y-1">
          {friends.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-6">Add friends first to create a group.</p>
          ) : friends.map((u) => (
            <button
              key={u.user_id}
              data-testid={`group-member-${u.user_id}`}
              onClick={() => toggle(u.user_id)}
              className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-accent transition-colors text-left"
            >
              <Avatar user={u} size={38} />
              <span className="text-sm font-medium flex-1 flex items-center gap-1">{u.name}{u.verified && <VerifiedBadge size={12} />}</span>
              <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${selected.includes(u.user_id) ? "bg-[#6C0BA9] border-[#6C0BA9]" : "border-border"}`}>
                {selected.includes(u.user_id) && <Check className="w-3 h-3 text-white" />}
              </span>
            </button>
          ))}
        </div>
        <Button data-testid="create-group-btn" onClick={create} disabled={busy || !name.trim() || selected.length < 1} className="w-full h-11 rounded-xl bg-[#6C0BA9] hover:bg-[#8012C6] text-white font-semibold gap-2">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : `Create Group${selected.length ? ` (${selected.length})` : ""}`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
